/**
 * PickingPrintModal - Modern print modal for picking operations
 * Uses printingService.ts with 4-level hierarchical configuration
 */
import React, { useState, useEffect } from 'react';
import {
  X, Tag, Package, FileText, MapPin, Loader2, Check,
  AlertCircle, ChevronLeft, Minus, Plus
} from 'lucide-react';
import { printingService } from '../../../../../services/printingService';
import {
  PrintItemLabelWithSORequest,
  PrintPackingSlipRequest,
  PrintLocationLabelRequest,
  PrintResult,
  PackingSlipLine,
  CreatePackageLabelsRequest,
  CreatePackageLabelsResponse,
  Report
} from '../../../../../types/printing';
import { PListDetailsDto, PListLineDto } from '../../../../../types/picking';
import { useAuth } from '../../../../../contexts/AuthContext';
import { usePrinterFallback } from '../../../../../hooks/usePrinterFallback';

// Hvilken print-tjeneste-metode som dispatcher for hver ReportType
type PrintDispatch = 'item' | 'itemWithPO' | 'itemWithSO' | 'package' | 'packingSlip' | 'location';

interface ReportTypeMetadata {
  dispatch: PrintDispatch;
  typeName: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  hoverColor: string;
}

/**
 * Mapper Reports.ReportType (1, 2, 3, 4, 10, 11, 40, 41 osv.) til UI-metadata og
 * dispatch-strategi. Returnerer null for typer som ikke skal vises i picking-printen
 * (f.eks. SSCC, sorteringskasse — har egne dialoger).
 */
const getReportMetadata = (reportType: number): ReportTypeMetadata | null => {
  switch (reportType) {
    case 1:
      return { dispatch: 'item', typeName: 'Vareetikett',
        icon: <Tag size={28} />, color: 'text-blue-600',
        bgColor: 'bg-blue-50', hoverColor: 'hover:bg-blue-100' };
    case 2:
      return { dispatch: 'itemWithPO', typeName: 'Vareetikett m/Innkjøpsordre',
        icon: <Tag size={28} />, color: 'text-indigo-600',
        bgColor: 'bg-indigo-50', hoverColor: 'hover:bg-indigo-100' };
    case 3:
      return { dispatch: 'itemWithSO', typeName: 'Vareetikett m/Salgsordre',
        icon: <Tag size={28} />, color: 'text-purple-600',
        bgColor: 'bg-purple-50', hoverColor: 'hover:bg-purple-100' };
    case 4:
    case 5:
      return { dispatch: 'location', typeName: 'Lokasjonsetikett',
        icon: <MapPin size={28} />, color: 'text-teal-600',
        bgColor: 'bg-teal-50', hoverColor: 'hover:bg-teal-100' };
    case 10:
    case 11:
      return { dispatch: 'packingSlip', typeName: 'Pakkseddel',
        icon: <FileText size={28} />, color: 'text-green-600',
        bgColor: 'bg-green-50', hoverColor: 'hover:bg-green-100' };
    case 40:
    case 41:
      return { dispatch: 'package', typeName: 'Kolli-etikett',
        icon: <Package size={28} />, color: 'text-orange-600',
        bgColor: 'bg-orange-50', hoverColor: 'hover:bg-orange-100' };
    default:
      return null;
  }
};

/**
 * Slår opp metadata for en dispatch-type ved å finne første rapport av den typen
 * og hente metadata via getReportMetadata. Brukes i steg 1 før konkret rapport er valgt.
 */
const getMetadataForDispatch = (dispatch: PrintDispatch, reports: Report[]): ReportTypeMetadata | null => {
  for (const r of reports) {
    const meta = getReportMetadata(r.reportType);
    if (meta?.dispatch === dispatch) return meta;
  }
  return null;
};

interface PickingPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  pickingDetails: PListDetailsDto | null;
  activeLine: PListLineDto | null;
}

export const PickingPrintModal: React.FC<PickingPrintModalProps> = ({
  isOpen,
  onClose,
  pickingDetails,
  activeLine
}) => {
  const { user } = useAuth();
  // Steg 1: brukeren velger en type (item, location, package, packingSlip, ...)
  const [selectedDispatch, setSelectedDispatch] = useState<PrintDispatch | null>(null);
  // Steg 2: brukeren velger en konkret rapport (Reports-rad) av valgt type
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [availableReports, setAvailableReports] = useState<Report[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printResult, setPrintResult] = useState<PrintResult | null>(null);
  const [packageResult, setPackageResult] = useState<CreatePackageLabelsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state for editable fields
  const [copies, setCopies] = useState(1);
  const [itemNo, setItemNo] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [quantity, setQuantity] = useState<number | null>(null);
  const [batch, setBatch] = useState('');
  const [locationName, setLocationName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [orderNoERP, setOrderNoERP] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Avledet metadata for valgt rapport (eller valgt type i steg 1)
  const selectedMetadata = selectedReport
    ? getReportMetadata(selectedReport.reportType)
    : (selectedDispatch ? getMetadataForDispatch(selectedDispatch, availableReports) : null);

  // Tilgjengelige rapporter filtrert på valgt dispatch-type (for dropdown i steg 2)
  const reportsForSelectedType = selectedDispatch
    ? availableReports.filter((r) => {
        const meta = getReportMetadata(r.reportType);
        return meta?.dispatch === selectedDispatch;
      })
    : [];

  // Unike dispatch-typer som finnes i tilgjengelige rapporter (for steg 1-grid)
  const availableDispatchTypes: PrintDispatch[] = Array.from(
    new Set(
      availableReports
        .map((r) => getReportMetadata(r.reportType)?.dispatch)
        .filter((d): d is PrintDispatch => d !== undefined)
    )
  );

  // Reset state when modal opens/closes + last rapporter fra Reports-tabellen
  useEffect(() => {
    if (!isOpen || !user?.companyNo) return;

    setSelectedDispatch(null);
    setSelectedReport(null);
    setPrintResult(null);
    setPackageResult(null);
    setError(null);
    setCopies(1);
    prefillFromActiveLine();

    let cancelled = false;
    setLoadingReports(true);
    setReportsError(null);
    printingService.getReports(user.companyNo, undefined, true)
      .then((reports) => {
        if (cancelled) return;
        // Vis kun rapporter med en gjenkjent type for picking-flyten
        const filtered = reports.filter((r) => getReportMetadata(r.reportType) !== null);
        // Sorter på type, deretter ReportID
        filtered.sort((a, b) => a.reportType - b.reportType || a.reportID - b.reportID);
        setAvailableReports(filtered);
      })
      .catch((err) => {
        if (!cancelled) setReportsError(err?.message || 'Kunne ikke laste rapporter');
      })
      .finally(() => {
        if (!cancelled) setLoadingReports(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.companyNo, activeLine]);

  // Auto-close modal after successful package label creation
  useEffect(() => {
    if (packageResult?.success) {
      const timer = setTimeout(() => {
        onClose();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [packageResult, onClose]);

  // Prefill form from active line
  const prefillFromActiveLine = () => {
    if (activeLine) {
      setItemNo(activeLine.itemNo || '');
      setQuantity(activeLine.qtyPicked || activeLine.qty);
      setLocationName(activeLine.binCode || '');
      setCustomerName(activeLine.customerName || '');
      setOrderNoERP(activeLine.orderNoERP || '');
    } else if (pickingDetails?.lines?.length) {
      // Use first line as fallback
      const firstLine = pickingDetails.lines[0];
      setItemNo(firstLine.itemNo || '');
      setQuantity(firstLine.qty);
      setLocationName(firstLine.binCode || '');
      setCustomerName(firstLine.customerName || '');
      setOrderNoERP(firstLine.orderNoERP || '');
    }
  };

  // Update form when selected report changes
  useEffect(() => {
    if (selectedReport) {
      prefillFromActiveLine();
    }
  }, [selectedReport]);

  const handleBack = () => {
    setSelectedDispatch(null);
    setSelectedReport(null);
    setPrintResult(null);
    setPackageResult(null);
    setError(null);
  };

  /**
   * Når brukeren velger en type i steg 1, går vi videre til steg 2 og auto-velger
   * første rapport av den typen i dropdownen. Brukeren kan så bytte til en annen.
   */
  const handleSelectDispatch = (dispatch: PrintDispatch) => {
    setSelectedDispatch(dispatch);
    const firstOfType = availableReports.find(
      (r) => getReportMetadata(r.reportType)?.dispatch === dispatch
    );
    setSelectedReport(firstOfType || null);
    setPrintResult(null);
    setPackageResult(null);
    setError(null);
  };

  const printerFallback = usePrinterFallback();

  /**
   * Bygger og kjører selve print-kallet basert på valgt rapport. Sender
   * `reportId: selectedReport.reportID` som override slik at backend bruker
   * rapportens egen LayoutJson hvis satt, ellers hardkodet generator.
   */
  const runPrint = async (printerOverride?: number): Promise<PrintResult> => {
    if (!user?.companyNo || !selectedReport || !selectedMetadata) {
      return { success: false, message: 'Mangler firma eller rapport', labelsPrinted: 0 };
    }

    const reportId = selectedReport.reportID;

    switch (selectedMetadata.dispatch) {
      case 'item':
        return await printingService.printItemLabel(user.companyNo, {
          reportId,
          itemNo,
          itemDescription,
          quantity,
          batch: batch || undefined,
          locationName: locationName || undefined,
          numOfCopies: copies,
          printerIdOverride: printerOverride
        });

      case 'itemWithPO':
        return await printingService.printItemLabelWithPO(user.companyNo, {
          reportId,
          itemNo,
          itemDescription,
          quantity,
          batch: batch || undefined,
          locationName: locationName || undefined,
          pOrderNo: activeLine?.orderNo,
          numOfCopies: copies,
          printerIdOverride: printerOverride
        } as any);

      case 'itemWithSO':
        return await printingService.printItemLabelWithSO(user.companyNo, {
          reportId,
          itemNo,
          itemDescription,
          quantity,
          batch: batch || undefined,
          locationName: locationName || undefined,
          orderNo: activeLine?.orderNo,
          orderNoERP: orderNoERP || undefined,
          customerName: customerName || undefined,
          numOfCopies: copies,
          printerIdOverride: printerOverride
        } as PrintItemLabelWithSORequest);

      case 'packingSlip': {
        const slipLines: PackingSlipLine[] = pickingDetails?.lines.map((line, idx) => ({
          lineNo: idx + 1,
          itemNo: line.itemNo || '',
          itemDescription: '',
          qtyOrdered: line.qty,
          qtyPicked: line.qtyPicked,
          location: line.binCode || undefined
        })) || [];

        return await printingService.printPackingSlip(user.companyNo, {
          reportId,
          pListId: pickingDetails?.header.pListID || 0,
          customerName: customerName || undefined,
          deliveryAddress: deliveryAddress || undefined,
          packingDate: new Date().toISOString(),
          lines: slipLines,
          numOfCopies: copies,
          printerIdOverride: printerOverride
        } as PrintPackingSlipRequest);
      }

      case 'location':
        return await printingService.printLocationLabel(user.companyNo, {
          reportId,
          locationId: locationName,
          locationName: locationName || undefined,
          binCode: locationName || undefined,
          includeBarcode: true,
          numOfCopies: copies,
          printerIdOverride: printerOverride
        } as PrintLocationLabelRequest);

      default:
        return { success: false, message: 'Ukjent print-type', labelsPrinted: 0 };
    }
  };

  const handlePrint = async () => {
    if (!user?.companyNo || !selectedReport || !selectedMetadata) return;

    setIsPrinting(true);
    setError(null);
    setPrintResult(null);
    setPackageResult(null);

    // Pakke-flyten bruker eget endepunkt (createAndPrintPackageLabels) og har annen
    // result-form — håndteres separat utenfor fallback-mekanismen.
    if (selectedMetadata.dispatch === 'package') {
      try {
        const packageRequest: CreatePackageLabelsRequest = {
          numberOfLabels: copies,
          pListID: pickingDetails?.header.pListID || 0,
          orderNo: activeLine?.orderNo || pickingDetails?.lines[0]?.orderNo || 0,
          pickType: 'Standalone',
          customerName: customerName || undefined,
          orderNoERP: orderNoERP || undefined,
          deliveryAddress: deliveryAddress || undefined,
          printLabels: true
        };

        const packageResponse = await printingService.createAndPrintPackageLabels(packageRequest);
        setPackageResult(packageResponse);
      } catch (err: any) {
        console.error('Print error:', err);
        setError(err.message || 'Feil ved utskrift');
      } finally {
        setIsPrinting(false);
      }
      return;
    }

    try {
      await printerFallback.tryPrint(
        () => runPrint(undefined),
        (printerId) => runPrint(printerId),
        (result) => setPrintResult(result)
      );
    } catch (err: any) {
      console.error('Print error:', err);
      setError(err.message || 'Feil ved utskrift');
    } finally {
      setIsPrinting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            {selectedDispatch && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Tilbake til valg"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedDispatch ? (selectedMetadata?.typeName ?? 'Velg rapport') : 'Velg utskriftstype'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Lukk"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {!selectedDispatch ? (
            // Steg 1: type-velger — viser kun typer som har minst én rapport tilgjengelig
            <div className="grid gap-3">
              {loadingReports && (
                <div className="flex items-center gap-2 p-4 text-gray-500">
                  <Loader2 size={18} className="animate-spin" />
                  <span>Laster rapporter…</span>
                </div>
              )}
              {reportsError && (
                <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">
                  {reportsError}
                </div>
              )}
              {!loadingReports && !reportsError && availableReports.length === 0 && (
                <div className="p-4 bg-gray-50 text-gray-600 rounded-lg text-sm">
                  Ingen rapporter funnet for denne tenanten. Opprett rapporter i Rapport-designer
                  med ReportType 1 (Vareetikett), 4 (Lokasjon), 10/11 (Pakkseddel) eller 40/41 (Kolli).
                </div>
              )}
              {availableDispatchTypes.map((dispatch) => {
                const meta = getMetadataForDispatch(dispatch, availableReports);
                if (!meta) return null;
                const count = availableReports.filter(
                  (r) => getReportMetadata(r.reportType)?.dispatch === dispatch
                ).length;
                return (
                  <button
                    key={dispatch}
                    onClick={() => handleSelectDispatch(dispatch)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 border-transparent
                      ${meta.bgColor} ${meta.hoverColor} transition-all duration-200
                      hover:border-current hover:shadow-md`}
                  >
                    <div className={`${meta.color} p-3 bg-white rounded-xl shadow-sm`}>
                      {meta.icon}
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <h3 className={`font-semibold ${meta.color} truncate`}>{meta.typeName}</h3>
                      <p className="text-sm text-gray-600">
                        {count} {count === 1 ? 'rapport' : 'rapporter'} tilgjengelig
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            // Detail/edit view
            <div className="space-y-4">
              {/* Success/Error messages */}
              {printResult && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  printResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {printResult.success ? (
                    <Check size={20} className="flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">{printResult.success ? 'Utskrift vellykket!' : 'Feil ved utskrift'}</p>
                    <p className="text-sm">{printResult.message}</p>
                    {printResult.labelsPrinted > 0 && (
                      <p className="text-sm mt-1">{printResult.labelsPrinted} etikett(er) skrevet ut</p>
                    )}
                  </div>
                </div>
              )}

              {/* Package creation result */}
              {packageResult && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  packageResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {packageResult.success ? (
                    <Check size={20} className="flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">
                      {packageResult.success ? 'Kolli-etiketter opprettet!' : 'Feil ved opprettelse'}
                    </p>
                    <p className="text-sm">{packageResult.message}</p>
                    {packageResult.success && packageResult.createdPackages.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium">Opprettede kollier:</p>
                        {packageResult.createdPackages.map((pkg) => (
                          <p key={pkg.packageID} className="text-xs">
                            {pkg.displayText} (ID: {pkg.packageID})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="p-4 rounded-lg bg-red-50 text-red-800 flex items-start gap-3">
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Feil</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Form fields based on selected type */}
              <div className="space-y-4">
                {/* Rapport-velger for valgt type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rapport
                  </label>
                  <select
                    value={selectedReport?.reportID ?? ''}
                    onChange={(e) => {
                      const id = parseInt(e.target.value, 10);
                      const r = reportsForSelectedType.find((x) => x.reportID === id);
                      setSelectedReport(r ?? null);
                      setPrintResult(null);
                      setPackageResult(null);
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    disabled={reportsForSelectedType.length === 0}
                  >
                    {reportsForSelectedType.length === 0 ? (
                      <option value="">Ingen rapporter for denne typen</option>
                    ) : (
                      reportsForSelectedType.map((r) => (
                        <option key={r.reportID} value={r.reportID}>
                          {r.description} (ID {r.reportID})
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {/* Copies - always shown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {selectedMetadata?.dispatch === 'package' ? 'Antall kolli-etiketter' : 'Antall kopier'}
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setCopies(Math.max(1, copies - 1))}
                      className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={copies <= 1}
                    >
                      <Minus size={18} />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={copies}
                      onChange={(e) => setCopies(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center p-3 border border-gray-300 rounded-lg text-lg font-semibold"
                    />
                    <button
                      type="button"
                      onClick={() => setCopies(Math.min(100, copies + 1))}
                      className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={copies >= 100}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>

                {/* Item fields */}
                {(selectedMetadata?.dispatch === 'item' || selectedMetadata?.dispatch === 'itemWithSO' || selectedMetadata?.dispatch === 'itemWithPO') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Varenummer
                      </label>
                      <input
                        type="text"
                        value={itemNo}
                        onChange={(e) => setItemNo(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="F.eks. 12345"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Antall
                      </label>
                      <input
                        type="number"
                        value={quantity || ''}
                        onChange={(e) => setQuantity(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="Antall enheter"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Batch/Lot (valgfritt)
                      </label>
                      <input
                        type="text"
                        value={batch}
                        onChange={(e) => setBatch(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="F.eks. LOT2024-001"
                      />
                    </div>
                  </>
                )}

                {/* Order fields */}
                {(selectedMetadata?.dispatch === 'itemWithSO' || selectedMetadata?.dispatch === 'itemWithPO') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kundenavn
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="Kundens navn"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ordrenummer (ERP)
                      </label>
                      <input
                        type="text"
                        value={orderNoERP}
                        onChange={(e) => setOrderNoERP(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="F.eks. SO-2024-0001"
                      />
                    </div>
                  </>
                )}

                {/* Package fields */}
                {selectedMetadata?.dispatch === 'package' && (
                  <>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-sm text-orange-800 font-medium">
                        Oppretter {copies} kolli-rad(er) i databasen
                      </p>
                      <p className="text-xs text-orange-700 mt-1">
                        Hver kolli får et unikt ID som skrives ut som strekkode.
                        PListID: {pickingDetails?.header.pListID}, Ordre: {activeLine?.orderNo || pickingDetails?.lines[0]?.orderNo}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kundenavn
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Leveringsadresse
                      </label>
                      <textarea
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        rows={2}
                        placeholder="Adresse for levering"
                      />
                    </div>
                  </>
                )}

                {/* Packing slip info */}
                {selectedMetadata?.dispatch === 'packingSlip' && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Pakkseddel genereres med alle linjer fra plukkliste #{pickingDetails?.header.pListID}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {pickingDetails?.lines.length || 0} linjer inkluderes
                    </p>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kundenavn (valgfritt)
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* Location fields */}
                {selectedMetadata?.dispatch === 'location' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lokasjon / Bin-kode
                    </label>
                    <input
                      type="text"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder="F.eks. A-01-01"
                    />
                  </div>
                )}

                {/* Context info */}
                {activeLine && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                    <p className="font-medium">Fra aktiv plukklinje:</p>
                    <p>Vare: {activeLine.itemNo}, Lokasjon: {activeLine.binCode}</p>
                  </div>
                )}
              </div>

              {/* Print button */}
              <button
                onClick={handlePrint}
                disabled={isPrinting || !selectedReport}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-all
                  ${(isPrinting || !selectedReport)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : `${selectedMetadata?.color.replace('text-', 'bg-')} hover:opacity-90`
                  }`}
              >
                {isPrinting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={20} className="animate-spin" />
                    Skriver ut...
                  </span>
                ) : (
                  `Skriv ut ${selectedMetadata?.typeName || ''}`
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Fallback-modal når ingen fast skriver er konfigurert */}
      {printerFallback.modal}
    </div>
  );
};

export default PickingPrintModal;
